<template>
    <div class="time-options">
        <el-dialog :visible.sync="dialogVisible" :show-close="false" :top="dialogTopMargin" :append-to-body="true" custom-class="time-options__dialog">
            <div class="time-options__content">
                <div class="time-options__head">
                    <span class="caption-bold">{{ title }}</span>
                     <div class="time-options__close" @click="closeClickHandler">
                        <icon><IconClose/></icon>
                    </div>
                </div>
                <div class="time-options__intro copy-light" v-if="type === 'traffic'">
                    Die hier eingestellte Zeit bezieht sich nur auf die Anzeige der Verkehrsprognose und das Reisedatum und hat keine Aus&shy;wirkungen auf andere Infos (z.B. Öffnungszeiten).
                </div>
                <div class="time-options__tabs" v-if="type === 'route'">
                    <div class="time-options__tabs__item chips-bold" v-for="modeSwitch in modes" :key="modeSwitch.value" :class="{'is-active': mode === modeSwitch.value}" @click="modeSwitchClickHandler(modeSwitch.value)">
                        {{ modeSwitch.label }}
                    </div>
                </div>
                <div class="time-options__timepicker">
                    <SmrTimeSelect :value="time" @change="timeSelectChangeHandler" />
                </div>
                <div class="time-options__fixed-times">
                    <div class="time-options__fixed-times__item chips" @click="fixedTimeClickHandler(0)">Jetzt</div>
                    <div class="time-options__fixed-times__item chips" @click="fixedTimeClickHandler(15)" v-if="type === 'route'">In 15 <template v-if="mobile">Min.</template><template v-else>Minuten</template></div>
                    <div class="time-options__fixed-times__item chips" @click="fixedTimeClickHandler(60)">In 1 <template v-if="mobile">Std.</template><template v-else>Stunde</template></div>
                    <div class="time-options__fixed-times__item chips" @click="fixedTimeClickHandler(120)" v-if="type === 'traffic'">In 2 <template v-if="mobile">Std.</template><template v-else>Stunden</template></div>
                </div>
                <div class="time-options__datepicker">
                    <SmrDatePicker :value="date" @change="datePickerChangeHandler"/>
                </div>
                <SmrButton @click.native="saveClickHandler">Übernehmen</SmrButton>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import SmrTimeSelect from '@/components/form/SmrTimeSelect.vue'
import SmrDatePicker from '@/components/form/SmrDatePicker.vue'
import SmrButton from '@/components/SmrButton.vue'
import Icon from '@/components/Icon.vue'
import IconClose from '@/components/icons/IconClose.vue'
import { formatTime } from '@/utils/format'
import eventBus from '@/utils/event-bus';
import { trackEvent, trackGa4Event } from '@/utils/tracking'

export default {
    components: {
        SmrTimeSelect,
        SmrDatePicker,
        SmrButton,
        Icon,
        IconClose
    },
    props: {
        title: {
            type: String,
            default: ''
        },
        type: {
            type: String,
            default: 'route'
        }
    },
    data() {
        return {
            dialogVisible: false,
            mode: 'departure',
            time: '',
            date: '',
            isNow: true,
            modes: [
                {
                    label: 'Abfahrt',
                    value: 'departure'
                },
                {
                    label: 'Ankunft',
                    value: 'arrival'
                },
            ],
        }
    },
    computed: {
        ...mapGetters(['mobile', 'routeTrafficTime', 'savedRouteTime']),
        dialogTopMargin() {
            return this.mobile ? '20px' : '5vh';
        }
    },
    watch: {
        savedRouteTime() {
            if(this.savedRouteTime) this.prepareTimeData()
        }
    },
    created() {
        this.addEventListeners()
    },
    mounted() {
        this.prepareTimeData()
    },
    methods: {
        addEventListeners() {
            eventBus.$on('setCurrentTime', this.setCurrentTime);
        },
        closeClickHandler() {
            this.closeDialog();
        },
        saveClickHandler() {
            this.trackingEvent();
            this.updateRouteTime()
            this.closeDialog()
        },
        trackingEvent() {
            if(this.title === 'Verkehrsprognose') {
                trackEvent({ category: 'Traffic', action: 'Select', label: 'Set Time'});
                trackGa4Event({ga4Parameter1Name: 'forecast', ga4Parameter1Value: 'set_time', ga4Event: 'traffic'});
            } else {
                trackEvent({ category: 'Route Planner', action: 'Time', label: this.mode === 'departure' ? 'Set Departure Time' : 'Set Arrival Time'});
                trackGa4Event({ga4Parameter1Name: 'route', ga4Parameter1Value:this.mode === 'departure' ? 'set_departure_time' : 'set_arrival_time', ga4Event: 'route_planner'})
            }
        },
        setCurrentTime() {
            this.time = formatTime(new Date())
            this.isNow = true
        },  
        timeSelectChangeHandler(data) {
            if(data === null) {
                this.time = formatTime(new Date())
                this.isNow = true
            } else {
               this.time = data
               this.isNow = false 
            }
        },
        datePickerChangeHandler(data) {
            this.date = data
            if(this.date.getTime() === new Date().getTime()) this.isNow = true;
            else this.isNow = false
        },
        fixedTimeClickHandler(minutesFromNow) {
            const now = new Date()
            if(minutesFromNow > 0) {
                this.isNow = false
                now.setMinutes(now.getMinutes() + minutesFromNow)
            }
            if(minutesFromNow === 0) {
                this.isNow = true
            }
            this.time = formatTime(now)
            this.date = now
        },

        prepareTimeData() {
            const storeTime = this.savedRouteTime || this.routeTrafficTime;
            if(storeTime && storeTime.date) {
                this.time = storeTime.time
                this.date = storeTime.date
                this.isNow = storeTime.isNow
                this.mode = storeTime.mode ? storeTime.mode : 'departure'
            } else {
                const now = new Date()
                this.time = formatTime(now)
                this.date = now
                this.mode = storeTime.mode ? storeTime.mode : 'departure'
            }
        },
        updateRouteTime() { 
            if (this.savedRouteTime) this.$store.commit('setSavedRouteTime', false) // allow change of date after opening saved route
            this.$store.commit('setRouteTrafficTime', {
                time: this.time === null ? formatTime(new Date()) : this.time,
                date: this.date,
                isNow: this.isNow,
                mode: this.mode
            })
        },
        closeDialog() {
            this.dialogVisible = false;
        },
        showDialog() {
            this.prepareTimeData()
            this.dialogVisible = true;
        },
        modeSwitchClickHandler(mode) {
            this.mode = mode;
        }
    }
}
</script>

<style lang="scss">
.time-options {
    $block: &;
    &__intro {
        margin-bottom: 16px;
    }
    &__dialog{
        max-width: 350px !important;
        margin: 5px auto 5vh 175px;

        @include mq($until: tablet) {
            margin: 20px auto;
            border-radius: 9px;
            width: calc(100% - 32px);
        }
        
        .el-dialog{
            &__header {
                display: none;
            }
            &__body {
                padding: 0;
                word-break: initial;
            }
        }
    }

    &__content {
        padding: 8px 16px 16px;
        color: $color-black;
    }

    &__head {
        position: relative;
        margin-bottom: 13px;
    }

    &__close {
        position: absolute;
        top: 0;
        right: 0;
        color: $color-grey-dark;
        transition: 0.2s;
        cursor: pointer;

        .no-touch &:hover {
            color: $color-black;
        }
    }

    &__tabs {
        background: $color-black-005;
        border-radius: 6px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        margin-bottom: 16px;

        &__item {
            height: 34px;
            text-align: center;
            @include flex-center;
            cursor: pointer;
            border-radius: 6px;

            .no-touch &:hover {
                background-color: $color-black-003;
            }

            &.is-active {
                background: $color-yellow;
                pointer-events: none;
                .no-touch &:hover {
                    background: $color-yellow;
                }
            }
        }
    }

    &__fixed-times {
        margin-top: 16px;
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        &__item {
            height: 30px;
            line-height: 30px !important;
            background: $color-black-005;
            border-radius: 400px;
            padding: 0 20px;
            margin-bottom: 6px;
            transition: 0.2s;
            cursor: pointer;

            @include mq($until: mobile) {
                padding: 0 16px;
            }

            &:not(:last-child) {
                margin-right: 6px;
            }

            .no-touch &:hover {
                background-color: $color-black-01;
            }
        }
    }

    &__datepicker {
        margin-bottom: 16px;
    }
}
</style>